import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiExternalLink, FiUser, FiCalendar, FiBookOpen, FiTag, FiBook, FiCheckCircle, FiChevronDown, FiChevronUp } = FiIcons;

const ResultCard = ({ result, index }) => {
  const [showFullAbstract, setShowFullAbstract] = useState(false);

  const getTypeColor = (type) => {
    const colors = {
      article: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
      book: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
      chapter: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
      thesis: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
      conference: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
      preprint: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
      review: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200'
    };
    return colors[type] || colors.article;
  };

  const getTypeLabel = (type) => {
    const labels = {
      article: 'Artículo',
      book: 'Libro',
      chapter: 'Capítulo',
      thesis: 'Tesis',
      conference: 'Conferencia',
      preprint: 'Preprint',
      review: 'Revisión'
    };
    return labels[type] || 'Documento';
  };

  const getLanguageLabel = (lang) => {
    const languages = {
      es: 'Español',
      en: 'Inglés',
      pt: 'Portugués',
      fr: 'Francés',
      de: 'Alemán',
      it: 'Italiano',
      zh: 'Chino',
      ja: 'Japonés',
      ko: 'Coreano',
      ru: 'Ruso'
    };
    return languages[lang] || lang.toUpperCase();
  };

  // Verificar si la URL es válida y funcional
  const isValidUrl = (url) => {
    if (!url) return false;
    try {
      const urlObj = new URL(url);
      return urlObj.protocol === 'http:' || urlObj.protocol === 'https:';
    } catch {
      return false;
    }
  };

  const handleUrlClick = (e) => {
    if (!isValidUrl(result.url)) {
      e.preventDefault();
      // Si la URL no es válida, realizar búsqueda en Google Scholar como fallback
      const searchQuery = encodeURIComponent(result.title);
      const fallbackUrl = `https://scholar.google.com/scholar?q=${searchQuery}`;
      window.open(fallbackUrl, '_blank', 'noopener,noreferrer');
    }
  };

  // Función para truncar abstract a 5 líneas aproximadamente (250 caracteres)
  const getTruncatedAbstract = (abstract) => {
    if (!abstract || abstract === 'Resumen no disponible') return abstract;
    if (abstract.length <= 250) return abstract;
    return abstract.substring(0, 250) + '...';
  };

  const shouldShowExpandButton = (abstract) => {
    return abstract && abstract !== 'Resumen no disponible' && abstract.length > 250;
  };

  return (
    <motion.div
      className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-200 dark:border-gray-700 p-6 hover:shadow-xl transition-shadow duration-300"
      whileHover={{ y: -2 }}
    >
      {/* Número de resultado y badges */}
      <div className="flex items-start justify-between mb-4">
        <span className="inline-flex items-center justify-center w-8 h-8 bg-indigo-100 dark:bg-indigo-900 text-indigo-700 dark:text-indigo-300 rounded-full text-sm font-semibold">
          {index + 1}
        </span>
        <div className="flex flex-wrap gap-2">
          <span className={`px-3 py-1 rounded-full text-xs font-medium ${getTypeColor(result.type)}`}>
            {getTypeLabel(result.type)}
          </span>
          <span className="px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200">
            {getLanguageLabel(result.language)}
          </span>
          {result.isAuthentic && (
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
              <SafeIcon icon={FiCheckCircle} className="w-3 h-3 mr-1" />
              Verificado
            </span>
          )}
        </div>
      </div>

      {/* Título */}
      <div className="mb-4">
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3 leading-tight">
          {result.title}
        </h3>
      </div>

      {/* Metadatos principales */}
      <div className="space-y-2 mb-4">
        {/* Autor */}
        <div className="flex items-start space-x-2">
          <SafeIcon icon={FiUser} className="w-4 h-4 text-gray-500 mt-0.5 flex-shrink-0" />
          <div>
            <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Autor: </span>
            <span className="text-sm text-gray-800 dark:text-gray-200">
              {result.authors.slice(0, 3).join(', ')}{result.authors.length > 3 ? ' et al.' : ''}
            </span>
          </div>
        </div>

        {/* Año */}
        <div className="flex items-center space-x-2">
          <SafeIcon icon={FiCalendar} className="w-4 h-4 text-gray-500 flex-shrink-0" />
          <div>
            <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Año: </span>
            <span className="text-sm text-gray-800 dark:text-gray-200">{result.year}</span>
          </div>
        </div>

        {/* Fuente */}
        <div className="flex items-center space-x-2">
          <SafeIcon icon={FiBookOpen} className="w-4 h-4 text-gray-500 flex-shrink-0" />
          <div>
            <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Fuente: </span>
            <span className="text-sm text-gray-800 dark:text-gray-200">{result.source}</span>
          </div>
        </div>
      </div>

      {/* Resumen */}
      <div className="mb-4">
        <div className="flex items-center space-x-2 mb-2">
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Resumen:</span>
        </div>
        <div className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed">
          {showFullAbstract ? result.abstract : getTruncatedAbstract(result.abstract)}
          
          {shouldShowExpandButton(result.abstract) && (
            <button
              onClick={() => setShowFullAbstract(!showFullAbstract)}
              className="ml-2 inline-flex items-center text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 text-sm font-medium"
            >
              {showFullAbstract ? (
                <>
                  Ver menos <SafeIcon icon={FiChevronUp} className="w-3 h-3 ml-1" />
                </>
              ) : (
                <>
                  Ver más <SafeIcon icon={FiChevronDown} className="w-3 h-3 ml-1" />
                </>
              )}
            </button>
          )}
        </div>
      </div>

      {/* URL o DOI */}
      <div className="mb-4">
        {result.doi && (
          <div className="flex items-start space-x-2 mb-2">
            <span className="text-sm font-medium text-gray-600 dark:text-gray-400">DOI: </span>
            <a
              href={result.doi}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 break-all"
            >
              {result.doi}
            </a>
          </div>
        )}
        {result.url && (
          <div className="flex items-start space-x-2">
            <span className="text-sm font-medium text-gray-600 dark:text-gray-400">URL: </span>
            <a
              href={result.url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 break-all"
            >
              {result.url}
            </a>
          </div>
        )}
      </div>

      {/* Palabras clave */}
      {result.keywords && result.keywords.length > 0 && (
        <div className="mb-4">
          <div className="flex items-center space-x-2 mb-2">
            <SafeIcon icon={FiTag} className="w-4 h-4 text-gray-500" />
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Palabras clave:
            </span>
          </div>
          <div className="flex flex-wrap gap-2">
            {result.keywords.slice(0, 6).map((keyword, index) => (
              <span
                key={index}
                className="px-2 py-1 bg-indigo-50 dark:bg-indigo-900 text-indigo-700 dark:text-indigo-300 rounded text-xs"
              >
                {keyword}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Footer con citaciones y botón */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pt-4 border-t border-gray-200 dark:border-gray-700">
        <div className="flex items-center space-x-4 text-sm text-gray-600 dark:text-gray-400">
          {result.citations > 0 && (
            <span>{result.citations.toLocaleString()} citaciones</span>
          )}
        </div>

        <div className="flex gap-2">
          {/* Botón Ver documento */}
          <motion.a
            href={result.url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleUrlClick}
            className="inline-flex items-center space-x-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-medium transition-colors"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <span>Ver documento</span>
            <SafeIcon icon={FiExternalLink} className="w-4 h-4" />
          </motion.a>

          {/* Botón DOI si está disponible */}
          {result.doi && (
            <motion.a
              href={result.doi}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center space-x-2 px-3 py-2 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg text-sm font-medium transition-colors"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              title="Acceder via DOI"
            >
              <span>DOI</span>
              <SafeIcon icon={FiExternalLink} className="w-3 h-3" />
            </motion.a>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default ResultCard;